
import { api } from './base';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';
import type { AppRole } from '@/shared/config';

export interface UserSearchResult {
  id: string;
  username: string | null;
  email: string | null;
  displayName: string;
  department?: string | null;
}

export interface UserListItem extends UserSearchResult {
  firstName: string | null;
  lastName: string | null;
  middleName?: string | null;
  orgPath?: string[];
  phone?: string | null;
  avatarUrl?: string | null;
  createdAt: string;
  client: { id: string; name: string } | null;
  clientManual: boolean;
  deactivatedAt?: string | null;
  isVip?: boolean;
}

export interface KeycloakSyncResult {
  trigger: 'schedule' | 'manual';
  startedAt: string;
  finishedAt: string;
  ok: boolean;
  error: string | null;
  keycloakUsers: number;
  ldapUsers: number;
  created: number;
  updated: number;
  deactivated: number;
  reactivated: number;
  claimsWritten: number;
  claimsSkipped: string | null;
  warnings: string[];
  withoutDn?: number;
  withoutPhone?: number;
  report?: KeycloakSyncReport;
}

export interface KeycloakSyncReport {
  list: { kcCount: number | null; received: number; unique: number; refetched: boolean; complete: boolean | null };
  dn: {
    fromList: number;
    fromDb: number;
    fromCard: number;
    missing: number;
    missingNotLinked: number;
    missingLinked: number;
    missingUnchecked: number;
    missingNotAd?: number;
    examples: string[];
  };
  phone: { fromList: number; fromDb: number; fromCard: number; missing: number; invalid: number; invalidExamples: string[] };
  names: { fromCn: number; fromKeycloak: number };
  deactivation?: { disabledInKeycloak: number; missingFromKeycloak: number; stillInKeycloak?: number; examples: string[] };
  keycloak?: KeycloakInsight;
  phoneGate?: { withPhone: number; phoneFromCard: number; skippedNoPhone: number; skippedExamples: string[] };
  duplicates?: {
    linked: number;
    newlyLinked: number;
    released: number;
    sharedPhones: number;
    examples: string[];
    sharedExamples: string[];
  };
  purge?: {
    ran: boolean;
    skipped: string | null;
    candidates: number;
    deleted: number;
    keptLoggedIn: number;
    hiddenInstead: number;
    wereCommitteeMembers: number;
    wereGroupMembers: number;
    examples: string[];
  };
  cards: {
    needed: number;
    requested: number;
    failed: number;
    notReached: number;
    failures: Record<string, number>;
    failedExamples: string[];
  };
  retries: { total: number; byReason: Record<string, number> };
  stagesMs: Record<string, number>;
}

export interface KeycloakInsight {
  onePage: number | null;
  providers: Array<{ id: string; name: string; kind: string; settings: string; inList: number }> | null;
  providersError: string | null;
  byLink: Record<string, number>;
  byLogin: Record<string, number>;
  noDnAttributes: Record<string, number>;
  noDnSample: { login: string; created: string | null; link: string; idp: string[]; attributes: Record<string, string> } | null;
}

export interface KeycloakSyncProgress {
  stage: string;
  done: number | null;
  total: number | null;
  at: string;
}

export interface KeycloakSyncStatus {
  running: boolean;
  last: KeycloakSyncResult | null;
  progress?: KeycloakSyncProgress | null;
}

export interface UserListResponse {
  data: UserListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface MeResponse {
  id: string;
  email: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  middleName?: string | null;
  department?: string | null;
  orgPath?: string[];
  name: string;
  roles: AppRole[];
  permissions?: string[];
  avatarUrl?: string | null;
  wikiAccess?: boolean;
  toursSeen?: Record<string, number>;
  showIdentity?: boolean;
}

export interface KeycloakClaimExplanation {
  key: string;
  roles: string[];
}

export interface KeycloakClaimsSnapshot {
  syncedAt: string | null;
  groups: KeycloakClaimExplanation[];
  realmRoles: KeycloakClaimExplanation[];
  ldapDn?: string | null;
}

export interface AssignableRole {
  key: AppRole;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  missing: string[];
}

export interface GrantablePermission {
  key: string;
  label: string;
  group: string;
  groupLabel: string;
  summary: string;
  risk: 'elevated' | null;
  grantable: boolean;
}

export interface UserRolesResponse {
  effectiveRoles: AppRole[];
  localRoles: AppRole[];
  keycloakRoles: AppRole[];
  keycloakClaims?: KeycloakClaimsSnapshot;
  note?: string;
  personalPermissions?: string[];
  assignableRoles?: AssignableRole[];
  catalog?: GrantablePermission[];
  self?: boolean;
}

export type EffectiveRoleSource = 'local' | 'keycloak' | 'both' | 'personal';

export interface EffectiveRoleRef {
  key: string;
  name: string;
  source: EffectiveRoleSource;
}

export interface EffectivePermission {
  key: string;
  label: string;
  group: string;
  groupLabel: string;
  summary: string;
  viaRoles: EffectiveRoleRef[];
}

export interface EffectivePermissionsResponse {
  roles: EffectiveRoleRef[];
  permissions: EffectivePermission[];
}

export const usersApi = {
  getMe: () => api.get<MeResponse>('/api/v1/users/me'),

  uploadAvatar: async (file: Blob): Promise<{ avatarUrl: string }> => {
    const token = await getValidToken();
    const form = new FormData();
    form.append('file', file, 'avatar.webp');
    const res = await fetch(`${config.apiUrl}/api/v1/users/me/avatar`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    const json = (await res.json().catch(() => ({}))) as {
      data?: { avatarUrl: string };
      error?: { message?: string };
    };
    if (!res.ok || !json.data) throw new Error(json.error?.message ?? `Не удалось загрузить фото (${res.status})`);
    return json.data;
  },

  removeAvatar: () => api.delete<{ avatarUrl: null }>('/api/v1/users/me/avatar'),

  setShowIdentity: (showIdentity: boolean) =>
    api.patch<{ showIdentity: boolean }>('/api/v1/users/me', { showIdentity }, { toast: false }),

  markTourSeen: (tourId: string, version: number) =>
    api.post<{ toursSeen: Record<string, number> }>(`/api/v1/users/me/tours/${tourId}`, { version }),

  search: (q: string, sync = false, staffOnly = false) =>
    api.get<UserSearchResult[]>('/api/v1/users/search', {
      q,
      sync: sync ? '1' : undefined,
      staff: staffOnly ? '1' : undefined,
      limit: 20,
    }),

  keycloakSyncStatus: () => api.get<KeycloakSyncStatus>('/api/v1/admin/users/sync'),

  startKeycloakSync: () => api.post<{ started: true }>('/api/v1/admin/users/sync', {}),

  list: (params?: { page?: number; limit?: number; q?: string; clientId?: string; vip?: '1' }) =>
    api.get<UserListResponse>('/api/v1/admin/users', params as Record<string, string | number>).then(
      (res) => (res as unknown as { data: UserListResponse['data']; meta: UserListResponse['meta'] })
    ),

  setVip: (userId: string, isVip: boolean) =>
    api.put<{ id: string; isVip: boolean }>(`/api/v1/admin/users/${encodeURIComponent(userId)}/vip`, { isVip }),

  getUserRoles: (userId: string) =>
    api.get<UserRolesResponse>(`/api/v1/admin/users/${encodeURIComponent(userId)}/roles`),

  getEffectivePermissions: (userId: string) =>
    api.get<EffectivePermissionsResponse>(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/effective-permissions`,
    ),

  addUserRole: (userId: string, roleName: AppRole) =>
    api.post<{ roleName: AppRole; localRoles: AppRole[] }>(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/roles`,
      { roleName }
    ),

  addPersonalPermission: (userId: string, permission: string) =>
    api.post<{ personalPermissions: string[] }>(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/permissions`,
      { permission },
    ),

  removePersonalPermission: (userId: string, permission: string) =>
    api.delete<{ personalPermissions: string[] }>(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/permissions/${encodeURIComponent(permission)}`,
    ),

  removeUserRole: (userId: string, roleName: string) =>
    api.delete<{ removed: string; localRoles: AppRole[] }>(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleName)}`
    ),
};
